import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  EVENTOS,
  GRUPOS_DA_NOTIFICACAO,
  PRIORIDADE_NOTIFICACAO,
  ROTULO_GRUPO_DA_NOTIFICACAO,
  ROTULO_PRIORIDADE,
  ROTULO_TIPO_NOTIFICACAO,
  TIPOS_NOTIFICACAO,
  grupoDaNotificacao,
  type GrupoDaNotificacao,
  type NotificacaoDto,
  type PrioridadeNotificacao,
  type TipoNotificacao,
} from '@crm/shared'
import {
  chavesOperacao,
  useDispensarNotificacao,
  useDispensarTodas,
  useNotificacoes,
  useNotificacoesDoGrupo,
} from '../api/hooks-operacao'
import { useQueryClient } from '@tanstack/react-query'
import { pegarSocket, soltarSocket } from '../tempo-real/socket'
import { formatarDataHora, plural } from '../formato'
import { AcoesDoAviso } from './AcoesDoAviso'
import { useFecharAoClicarFora } from './fechar-fora'
import { IconeSino } from './icones'
import { MARCA_DO_GRUPO, SimboloDoAviso } from './MarcaDeAviso'
import { Botao, juntar } from './ui'

/**
 * Notificações (5.6): tarefas do dia, avisos de supervisão e cronômetro
 * estourado. Contador dentro do sistema como padrão.
 *
 * O alerta de cronômetro tem tratamento próprio: a especificação pede que ele
 * apareça no canto da tela e **permaneça até o usuário clicar no X**. Por isso
 * ele é dispensado no servidor, não escondido no estado local — um F5 não
 * ressuscita alerta que a pessoa já mandou embora.
 */

export function SinoDeNotificacoes() {
  const [aberto, setAberto] = useState(false)
  const [tipo, setTipo] = useState<TipoNotificacao | 'todos'>('todos')
  const { data } = useNotificacoes(tipo)
  const dispensar = useDispensarNotificacao()
  const dispensarTodas = useDispensarTodas()
  const navegar = useNavigate()

  const pendentes = data?.pendentes ?? 0
  const itens = data?.itens ?? []

  /*
    Fechar o painel devolve o filtro para "todos".

    Um recorte que sobrevive ao fechamento fica invisível: a pessoa filtra por
    tarefa, fecha, volta meia hora depois e vê a lista quase vazia com o sino
    marcando dezoito. A conclusão natural é que o sistema perdeu avisos, não
    que ela mesma deixou um filtro ligado.
  */
  function fechar() {
    setAberto(false)
    setTipo('todos')
  }

  const caixa = useFecharAoClicarFora<HTMLDivElement>(aberto, fechar)

  function irPara(notificacao: NotificacaoDto) {
    // Com o id do chamado, para o bate-papo já abrir na conversa avisada.
    const destino = destinoDaNotificacao(notificacao)
    if (destino) navegar(destino)
    fechar()
  }

  return (
    <div ref={caixa} className="relative">
      {/*
        Só o sino, com a contagem por cima. Na barra do topo o espaço é
        disputado pela navegação, e "Notificações" escrito por extenso tomava
        a largura de mais um item de menu para dizer o que o desenho já diz.
      */}
      <button
        type="button"
        // Fechar pelo próprio sino passa por `fechar()` como qualquer outra
        // saída, senão o filtro sobreviveria justamente pelo caminho mais
        // usado para fechar o painel.
        onClick={() => (aberto ? fechar() : setAberto(true))}
        aria-label={pendentes > 0 ? `Notificações (${pendentes})` : 'Notificações'}
        title={pendentes > 0 ? plural(pendentes, 'notificação', 'notificações') : 'Notificações'}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-tinta-600 transition hover:bg-white/8 hover:text-tinta-900"
      >
        <IconeSino className="h-4 w-4" />
        {pendentes > 0 ? (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
            {pendentes > 99 ? '99+' : pendentes}
          </span>
        ) : null}
      </button>

      {aberto ? (
        // No celular, a largura da tela com 8px de folga: preso à direita do sino com 320px, saía pela esquerda em tela de 360px.
        <div className="fixed inset-x-2 top-14 z-30 overflow-hidden rounded-2xl bg-superficie shadow-alta ring-1 ring-tinta-200 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80">
          <header className="border-b border-tinta-200 px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold">Notificações</span>
              {pendentes > 0 ? (
                <Botao variante="texto" onClick={() => dispensarTodas.mutate()}>
                  {/*
                    "Limpar tudo" quando há filtro, porque é o que o botão faz:
                    ele dispensa todas as pendentes, não só as que estão à
                    vista. Com o rótulo curto e a lista recortada, clicar
                    parecia limpar aquele tipo — e levava embora o resto.
                  */}
                  {tipo === 'todos' ? 'Limpar' : 'Limpar tudo'}
                </Botao>
              ) : null}
            </div>

            {/*
              O recorte em linha própria, e não ao lado do título: "Cronômetro
              estourado" não cabe num seletor espremido entre um título e um
              botão dentro de 320px, e sairia cortado justamente no rótulo mais
              longo.

              A lista é só de tipos, sem cabeçalho de prioridade. Chegou a vir
              agrupada por "Crítica", "Alta" e afins, para o agrupamento
              explicar a cor — mas quem abre o sino quer escolher um assunto,
              não estudar a escala. A prioridade continua onde ela serve: na
              cor do aviso.
            */}
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoNotificacao | 'todos')}
              aria-label="Filtrar por tipo"
              className="mt-1.5 w-full rounded-md bg-tinta-50 px-2 py-1 text-xs text-tinta-700 ring-1 ring-tinta-200 outline-none hover:bg-tinta-100 focus:ring-2 focus:ring-marca"
            >
              <option value="todos">Todos os tipos</option>
              {TIPOS_NOTIFICACAO.map((t) => (
                <option key={t} value={t}>
                  {ROTULO_TIPO_NOTIFICACAO[t]}
                </option>
              ))}
            </select>
          </header>

          <ul className="max-h-96 overflow-y-auto">
            {itens.length === 0 ? (
              /*
                Vazio por filtro não é o mesmo que vazio de verdade. Com o
                recorte ligado, "Nada por aqui" faria parecer que não há aviso
                nenhum — e o número no sino, dizendo o contrário, viraria
                defeito aparente.
              */
              <li className="p-6 text-center text-sm text-tinta-400">
                {tipo === 'todos' ? (
                  'Nada por aqui.'
                ) : (
                  <>
                    Nenhuma de {ROTULO_TIPO_NOTIFICACAO[tipo].toLowerCase()}.
                    <button
                      type="button"
                      onClick={() => setTipo('todos')}
                      className="mt-1 block w-full text-marca underline"
                    >
                      ver todos os tipos
                    </button>
                  </>
                )}
              </li>
            ) : (
              itens.map((n) => (
                <li
                  key={n.id}
                  className={juntar(
                    'border-b border-tinta-100 last:border-0',
                    // Já dispensada fica apagada: está ali como registro, não
                    // como coisa a fazer.
                    n.dispensadaEm && 'opacity-50',
                  )}
                >
                  <div className="flex items-start gap-2 p-2">
                    <button type="button" onClick={() => irPara(n)} className="flex-1 text-left">
                      {/*
                        A mesma paleta do cartão de canto, pela mesma
                        prioridade. O sino tinha regra de cor própria, escrita
                        por tipo — e um aviso mudava de cor entre a lista e o
                        cartão, o que fazia parecer que eram avisos diferentes.
                      */}
                      <span
                        className={juntar(
                          'text-xs font-semibold uppercase',
                          aparenciaDoAviso(n.tipo).texto,
                        )}
                        title={`Prioridade ${PRIORIDADE_NOTIFICACAO[n.tipo]}, ${ROTULO_PRIORIDADE[PRIORIDADE_NOTIFICACAO[n.tipo]]}`}
                      >
                        {ROTULO_TIPO_NOTIFICACAO[n.tipo]}
                      </span>
                      <p className="text-sm text-tinta-900">{n.titulo}</p>
                      {n.corpo ? <p className="text-xs text-tinta-400">{n.corpo}</p> : null}
                      <p className="text-[10px] text-tinta-400">{formatarDataHora(n.criadoEm)}</p>
                    </button>
                    {n.dispensadaEm ? null : (
                      <button
                        type="button"
                        onClick={() => dispensar.mutate(n.id)}
                        className="rounded px-1.5 text-tinta-400 hover:bg-tinta-100 hover:text-tinta-900"
                        aria-label="Dispensar"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </li>
              ))
            )}
          </ul>

          {/*
            O caminho para a tela cheia.

            O painel mostra as vinte primeiras pendentes, que é o que cabe num
            menu suspenso sem virar rolagem infinita. Quem precisa procurar
            um aviso antigo, ou reler o que já dispensou, precisa de uma tela
            — com filtro por tipo e paginação, que não cabem aqui.
          */}
          <footer className="border-t border-tinta-200 p-2">
            <Link
              to="/notificacoes"
              onClick={fechar}
              className="block rounded px-2 py-1.5 text-center text-sm font-medium text-marca hover:bg-marca/5"
            >
              Ver todas as notificações
            </Link>
          </footer>
        </div>
      ) : null}
    </div>
  )
}

/**
 * Para onde a notificação leva, ou `null` quando ela não leva a lugar nenhum.
 *
 * Ia para `/bate-papo` e parava aí: sem o id, a tela abria no que já estava
 * selecionado, e quem já estivesse no bate-papo não via nada acontecer — o
 * clique parecia quebrado. Com o id na URL, a conversa avisada abre mesmo que
 * esteja fora do filtro atual.
 *
 * A tela de Notificações usa a mesma: ela tinha uma cópia que ainda ia para `/bate-papo` sem id.
 */
export function destinoDaNotificacao(notificacao: NotificacaoDto): string | null {
  if (notificacao.entidade === 'chamado') {
    return notificacao.entidadeId ? `/bate-papo?chamado=${notificacao.entidadeId}` : '/bate-papo'
  }
  if (notificacao.entidade === 'tarefa') return '/calendario'
  if (notificacao.entidade === 'contato') return '/contatos'
  // Marketing (ADR 0057): o id do post vem antes do "#" (o "#" separa aprovar, aprovado, na hora).
  if (notificacao.entidade === 'post_marketing' && notificacao.entidadeId) {
    return `/marketing/posts/${notificacao.entidadeId.split('#')[0]}`
  }
  return null
}

/** O texto do link muda com o destino: "abrir a conversa" não serve para tarefa. */
function rotuloDoDestino(entidade: string | null): string {
  if (entidade === 'tarefa') return 'abrir o calendário'
  if (entidade === 'contato') return 'abrir o lead'
  if (entidade === 'post_marketing') return 'abrir o post'
  return 'abrir a conversa'
}

/*
  Quanto tempo o aviso passageiro fica na tela.

  Nove segundos: o suficiente para ler duas linhas sem pressa, e curto o
  bastante para não virar um painel permanente empilhado no canto. Ele não se
  perde ao sumir — continua pendente no sino, que é o lugar onde as coisas
  ficam.
*/
const DURACAO_NA_TELA = 9000
/** Casado com a animação `painel-da-borda-fechando` do CSS. */
const DURACAO_DA_SAIDA = 220

/**
 * A paleta dos quatro degraus de prioridade.
 *
 * Indexada pela prioridade, e não pelo tipo: é o que garante que dois avisos
 * de mesma prioridade saiam com a mesma cor mesmo sendo de assuntos
 * diferentes. Quem olha aprende quatro cores, não seis, e consegue ler um
 * tipo de aviso que ainda não conhece.
 *
 * A rampa desce em temperatura junto com a urgência — vermelho, âmbar, azul,
 * cinza —, então a ordem se lê sem legenda.
 */
const PALETA_DA_PRIORIDADE: Record<
  PrioridadeNotificacao,
  {
    caixa: string
    etiqueta: string
    titulo: string
    corpo: string
    acao: string
    fechar: string
    /** Só a cor do texto, para a lista do sino, que não tem caixa colorida. */
    texto: string
  }
> = {
  1: {
    caixa: 'ring-red-300 bg-red-50',
    etiqueta: 'text-red-600',
    titulo: 'text-red-800',
    corpo: 'text-red-700',
    acao: 'text-red-800',
    fechar: 'text-red-400 hover:bg-red-100 hover:text-red-800',
    texto: 'text-red-600',
  },
  2: {
    caixa: 'ring-amber-300 bg-amber-50',
    etiqueta: 'text-amber-700',
    titulo: 'text-amber-900',
    corpo: 'text-amber-800',
    acao: 'text-amber-900',
    fechar: 'text-amber-500 hover:bg-amber-100 hover:text-amber-900',
    texto: 'text-amber-600',
  },
  3: {
    caixa: 'ring-blue-300 bg-blue-50',
    etiqueta: 'text-blue-700',
    titulo: 'text-blue-900',
    corpo: 'text-blue-800',
    acao: 'text-blue-900',
    fechar: 'text-blue-400 hover:bg-blue-100 hover:text-blue-900',
    texto: 'text-blue-600',
  },
  4: {
    /*
      O último degrau não ganha cor nenhuma, de propósito. "Importação
      concluída" não pede nada de ninguém — pintar esse aviso gastaria a
      atenção que os três degraus acima precisam ter.
    */
    caixa: 'ring-tinta-200 bg-superficie',
    etiqueta: 'text-tinta-400',
    titulo: 'text-tinta-900',
    corpo: 'text-tinta-500',
    acao: 'text-tinta-600',
    fechar: 'text-tinta-400 hover:bg-tinta-100 hover:text-tinta-900',
    texto: 'text-tinta-400',
  },
}

function aparenciaDoAviso(tipo: NotificacaoDto['tipo']) {
  return PALETA_DA_PRIORIDADE[PRIORIDADE_NOTIFICACAO[tipo]]
}

function CartaoDeAviso({
  notificacao,
  aoFechar,
}: {
  notificacao: NotificacaoDto
  aoFechar: () => void
}) {
  const navegar = useNavigate()
  const prioridade = PRIORIDADE_NOTIFICACAO[notificacao.tipo]
  const cor = PALETA_DA_PRIORIDADE[prioridade]
  const destino = destinoDaNotificacao(notificacao)

  return (
    <div
      className={juntar('flex items-start gap-2 rounded-lg p-3 ring-1', cor.caixa)}
      role="status"
    >
      <div className="min-w-0 flex-1">
        {/*
          O tipo escrito por cima do título.

          Antes só o cronômetro chegava aqui, e um cartão vermelho no canto só
          podia ser ele. Agora que os seis aparecem, o tipo precisa se
          identificar — a cor diz o quanto corre, não de que assunto se trata,
          e tipos de mesma prioridade chegam da mesma cor de propósito.

          A prioridade em si fica no `title`: quem quiser conferir por que
          aquele aviso está vermelho passa o mouse, sem que a barra ganhe mais
          um número escrito para todo mundo ler todo dia.
        */}
        <span
          className={juntar('text-[10px] font-semibold uppercase', cor.etiqueta)}
          title={`Prioridade ${prioridade}, ${ROTULO_PRIORIDADE[prioridade]}`}
        >
          {ROTULO_TIPO_NOTIFICACAO[notificacao.tipo]}
        </span>
        <p className={juntar('text-sm font-semibold', cor.titulo)}>{notificacao.titulo}</p>
        {notificacao.corpo ? (
          <p className={juntar('text-xs', cor.corpo)}>{notificacao.corpo}</p>
        ) : null}
        {/*
          Sem destino não existe nada para abrir, e um link que leva à tela
          genérica parece o botão quebrado — o mesmo sintoma que já foi
          relatado uma vez aqui.
        */}
        {destino ? (
          <button
            type="button"
            onClick={() => navegar(destino)}
            className={juntar('mt-1 text-xs font-medium underline', cor.acao)}
          >
            {rotuloDoDestino(notificacao.entidade)}
          </button>
        ) : null}
        <AcoesDoAviso notificacao={notificacao} classeDoBotao={cor.acao} />
      </div>
      <button
        type="button"
        onClick={aoFechar}
        className={juntar('rounded px-1.5', cor.fechar)}
        aria-label="Dispensar aviso"
      >
        ×
      </button>
    </div>
  )
}

/** Quantos avisos pendentes cada marca tem, e quais marcas aparecem. */
function useMarcasDaBorda() {
  const { data } = useNotificacoes()
  const contagem: Record<GrupoDaNotificacao, number> = { cliente: 0, acao: 0, informa: 0 }
  for (const tipo of TIPOS_NOTIFICACAO) {
    contagem[grupoDaNotificacao(tipo)] += data?.pendentesPorTipo?.[tipo] ?? 0
  }
  const comAviso = GRUPOS_DA_NOTIFICACAO.filter((g) => contagem[g] > 0)
  return { data, contagem, comAviso }
}

/**
 * A barra de avisos na borda direita da tela (ADR 0035).
 *
 * Desenho do Mike: a caixa de avisos fica fora da tela, e na borda aparece só
 * um pedaço dela, com as três marcas empilhadas, cada uma com o seu contador:
 * o "!" vermelho para cliente esperando, o triângulo amarelo para o resto que
 * pede ação, a bolinha verde para o que só informa. Só aparece a marca que
 * tem aviso. Clicar numa marca abre a caixa daquela cor, crescendo da direita
 * para a esquerda até o tamanho da caixa de antes; clicar fora, apertar Esc
 * ou clicar na marca de novo recolhe.
 *
 * Aviso novo abre a caixa sozinho, por alguns segundos, e recolhe: o que
 * acabou de chegar não passa despercebido. Passar o mouse por cima segura a
 * caixa aberta.
 *
 * O X de cada aviso dispensa de verdade, no servidor. Antes, fora o
 * cronômetro, ele só escondia o cartão; agora o contador da marca é o lembrete
 * que fica, e esconder sem dispensar deixaria o número mentindo.
 */
export function BarraDeAvisos() {
  const cliente = useQueryClient()
  /*
    Aviso novo aparece na hora (mapa de uso, 08/10/2026). O servidor já
    mandava o sinal para a pessoa, mas ninguém escutava: o sino e esta barra
    só viam o aviso na consulta de minuto em minuto.
  */
  useEffect(() => {
    const socket = pegarSocket()
    const recarregar = () =>
      void cliente.invalidateQueries({ queryKey: chavesOperacao.notificacoes })
    socket.on(EVENTOS.notificacaoNova, recarregar)
    socket.on(EVENTOS.notificacoesMudaram, recarregar)
    return () => {
      socket.off(EVENTOS.notificacaoNova, recarregar)
      socket.off(EVENTOS.notificacoesMudaram, recarregar)
      soltarSocket()
    }
  }, [cliente])
  const { data, contagem, comAviso } = useMarcasDaBorda()
  const dispensar = useDispensarNotificacao()
  const [aberto, setAberto] = useState<{ grupo: GrupoDaNotificacao; sozinho: boolean } | null>(null)
  const [fechando, setFechando] = useState(false)
  const conhecidas = useRef(new Set<string>())
  const primeiraLeitura = useRef(true)
  const relogio = useRef<number | null>(null)

  const pararRelogio = () => {
    if (relogio.current !== null) window.clearTimeout(relogio.current)
    relogio.current = null
  }

  const fechar = useCallback(() => {
    pararRelogio()
    setFechando(true)
    window.setTimeout(() => {
      setAberto(null)
      setFechando(false)
    }, DURACAO_DA_SAIDA)
  }, [])

  const abrir = useCallback(
    (grupo: GrupoDaNotificacao, sozinho: boolean) => {
      pararRelogio()
      setFechando(false)
      setAberto({ grupo, sozinho })
      if (sozinho) relogio.current = window.setTimeout(fechar, DURACAO_NA_TELA)
    },
    [fechar],
  )

  const caixa = useFecharAoClicarFora<HTMLDivElement>(aberto !== null && !fechando, fechar)

  // Aviso novo abre sozinho a caixa da marca mais urgente entre os que chegaram.
  useEffect(() => {
    if (!data) return
    const pendentes = data.itens.filter((n) => !n.dispensadaEm)
    if (primeiraLeitura.current) {
      // O que já estava pendente ao abrir a tela não abre nada: o contador já diz.
      primeiraLeitura.current = false
      for (const n of pendentes) conhecidas.current.add(n.id)
      return
    }
    const novas = pendentes.filter((n) => !conhecidas.current.has(n.id))
    for (const n of pendentes) conhecidas.current.add(n.id)
    if (novas.length === 0) return
    // Quem abriu uma caixa com a mão está lendo: não se troca por cima dele.
    if (aberto && !aberto.sozinho) return
    const grupo = GRUPOS_DA_NOTIFICACAO.find((g) =>
      novas.some((n) => grupoDaNotificacao(n.tipo) === g),
    )
    if (grupo) abrir(grupo, true)
    // `aberto` fica de fora de propósito: a decisão é só na chegada.
  }, [data, abrir])

  // A marca zerou com a caixa aberta: não há mais o que mostrar.
  useEffect(() => {
    if (aberto && contagem[aberto.grupo] === 0 && !fechando) fechar()
  })

  useEffect(() => pararRelogio, [])

  if (comAviso.length === 0 && !aberto) return null

  return (
    /*
      No mesmo ponto do cartão de antes (120px do topo), que passa das barras
      de comando de todas as telas. A caixa nasce colada nas marcas e cresce
      para a esquerda; as marcas ficam presas na borda.

      As marcas são uma aba fina (24px, a mesma margem das telas), com o símbolo em cima e o número
      embaixo, por cima da página (escolha do Mike). A primeira versão, com
      símbolo e número lado a lado (56px), cobria o fim do que encostava na
      direita, como o "Selecionar" dos Leads; reservar uma faixa para ela
      deixava a tela cortada antes da borda. Fina, cabe na margem das telas e
      só passa por cima da barra de rolagem.
    */
    <div
      ref={caixa}
      className="fixed right-0 top-30 z-40 flex items-start"
    >
      {aberto ? (
        <CaixaDaMarca
          grupo={aberto.grupo}
          quantidade={contagem[aberto.grupo]}
          fechando={fechando}
          aoSegurar={() => {
            // O mouse em cima segura a caixa: quem está lendo não perde o texto.
            if (aberto.sozinho) {
              pararRelogio()
              setAberto({ grupo: aberto.grupo, sozinho: false })
            }
          }}
          aoFechar={fechar}
          aoDispensar={(id) => dispensar.mutate(id)}
        />
      ) : null}

      {comAviso.length > 0 ? (
        <div
          role="group"
          aria-label="Avisos"
          className="flex flex-col overflow-hidden rounded-l-lg bg-superficie shadow-media ring-1 ring-tinta-200"
        >
          {comAviso.map((grupo) => {
            const ativa = aberto?.grupo === grupo && !fechando
            return (
              <button
                key={grupo}
                type="button"
                aria-pressed={ativa}
                aria-label={`${ROTULO_GRUPO_DA_NOTIFICACAO[grupo]}: ${contagem[grupo]}`}
                title={ROTULO_GRUPO_DA_NOTIFICACAO[grupo]}
                onClick={() => (ativa ? fechar() : abrir(grupo, false))}
                className={juntar(
                  'flex w-6 flex-col items-center justify-center gap-0.5 border-b border-tinta-100 py-1.5 text-[11px] font-semibold leading-none tabular-nums text-tinta-800 transition last:border-b-0 hover:bg-tinta-50',
                  ativa && 'bg-tinta-100',
                )}
              >
                <SimboloDoAviso marca={MARCA_DO_GRUPO[grupo]} className="h-4 w-4 shrink-0" />
                {contagem[grupo] > 99 ? '99+' : contagem[grupo]}
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

/** A caixa de uma marca: os avisos pendentes daquela cor, o mais novo em cima. */
function CaixaDaMarca({
  grupo,
  quantidade,
  fechando,
  aoSegurar,
  aoFechar,
  aoDispensar,
}: {
  grupo: GrupoDaNotificacao
  quantidade: number
  fechando: boolean
  aoSegurar: () => void
  aoFechar: () => void
  aoDispensar: (id: string) => void
}) {
  const { data, isLoading } = useNotificacoesDoGrupo(grupo)
  const itens = (data?.itens ?? []).filter((n) => !n.dispensadaEm)

  return (
    <div
      onMouseEnter={aoSegurar}
      className={juntar(
        'mr-1.5 w-80 overflow-hidden rounded-2xl bg-superficie shadow-alta ring-1 ring-tinta-200',
        fechando ? 'painel-da-borda-fechando' : 'painel-da-borda-abrindo',
      )}
      role="dialog"
      aria-label={ROTULO_GRUPO_DA_NOTIFICACAO[grupo]}
    >
      <header className="flex items-center gap-2 border-b border-tinta-100 px-3 py-2">
        <SimboloDoAviso marca={MARCA_DO_GRUPO[grupo]} className="h-4.5 w-4.5 shrink-0" />
        <p className="min-w-0 flex-1 text-sm font-semibold text-tinta-900">
          {ROTULO_GRUPO_DA_NOTIFICACAO[grupo]}
        </p>
        <span className="text-xs tabular-nums text-tinta-400">{quantidade}</span>
        <button
          type="button"
          onClick={aoFechar}
          aria-label="Recolher os avisos"
          className="rounded px-1.5 text-tinta-400 hover:bg-tinta-100 hover:text-tinta-900"
        >
          ×
        </button>
      </header>

      <div className="max-h-[60vh] space-y-2 overflow-y-auto p-2">
        {isLoading ? (
          <p className="py-4 text-center text-xs text-tinta-400">Carregando…</p>
        ) : itens.length === 0 ? (
          <p className="py-4 text-center text-xs text-tinta-400">Nenhum aviso pendente.</p>
        ) : (
          itens.map((n) => (
            <CartaoDeAviso key={n.id} notificacao={n} aoFechar={() => aoDispensar(n.id)} />
          ))
        )}
      </div>

      <footer className="border-t border-tinta-100 px-3 py-2 text-right">
        <Link to="/notificacoes" className="text-xs font-medium text-marca hover:underline">
          Ver todas as notificações
        </Link>
      </footer>
    </div>
  )
}
